import {ref} from 'vue';
import {useLocationFromRoute} from '@navbar/common/composables';
import {navbarEventBus} from '@navbar/events';
import {useSearchRouteDefinitions} from '@navbar/features/search';
import {maxLength, minLength, regex, required} from '@regle/rules';
import {defineStore} from 'pinia';
import {useGuestHost, useRoute} from '#imports';
import {useRegle} from '@malt/forms';
import type {LocationItem} from './search.types';
import {redirectToSearch} from './utils';

export const useSearchFormStore = defineStore('navbar-search-form', () => {
    const {currentPage} = useSearchRouteDefinitions();
    const route = useRoute();
    const host = useGuestHost();

    const jobQuery = ref<string>((route.query.q as string) ?? '');
    const locationQuery = ref<string>((route.query.location as string) ?? '');

    const {r$} = useRegle(
        {
            jobQuery,
            locationQuery,
        },
        {
            jobQuery: {required, minLength: minLength(1), maxLength: maxLength(128), pattern: regex(/^.*\S.*$/)},
            locationQuery: {minLength: minLength(2), maxLength: maxLength(500)},
        },
        {id: 'navbar-search-form'},
    );

    const fullJobSuggestion = ref<string | null>(r$.$value.jobQuery);
    const fullLocationSuggestion = ref<LocationItem | null>(useLocationFromRoute() as LocationItem | null);

    const isJobFocused = ref(false);
    const jobZoomEffect = ref(false);
    const isLocationFocused = ref(false);

    function onSubmit() {
        if (currentPage.value?.name === 'Search') {
            navbarEventBus.emit('navbar-search-requested', {
                query: jobQuery.value,
                location: fullLocationSuggestion.value,
                sourceComponent: 'navbar_search',
            });
        } else if (fullJobSuggestion.value) {
            const searchUrl = redirectToSearch({
                baseURLLink: `${host}/s`,
                location: fullLocationSuggestion.value,
                query: fullJobSuggestion.value,
                sourceComponent: 'navbar_search',
            });
            window.location.href = searchUrl;
        }
    }

    return {
        r$,
        jobQuery,
        locationQuery,
        onSubmit,
        isJobFocused,
        jobZoomEffect,
        isLocationFocused,
        fullJobSuggestion,
        fullLocationSuggestion,
    };
});
